import { Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'modules/Integrations/domain'
import invariant from 'tiny-invariant'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { SphereIntegrationConfig } from 'Integrations/integrationsConfig/sphere/sphere.integration.config'
import SphereAuthenticationCard from './cards/SphereAuthenticationCard'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'

export const SphereConfigurationManagement = ({
  updateValidationState,
  updateConfiguration,
  integration
}: ConfigurationManagementProps) => {
  invariant(integration.id === 'Sphere', 'Integration is not Sphere')

  const credentialsQuery = useQuery(
    dashboardv99990101Client.getStoredSphereCredentials
  )

  const sphereConfiguration = useMemo(() => {
    if (credentialsQuery.data) {
      return {
        obfuscatedApiKey: credentialsQuery.data.obfuscatedApiKey
      } as SphereIntegrationConfig
    }

    return (
      (integration.configuration as SphereIntegrationConfig) || {
        obfuscatedApiKey: ''
      }
    )
  }, [credentialsQuery.data, integration.configuration])

  const [wasConnectionSuccessful, setWasConnectionSuccessful] = useState(false)
  const [pendingCredentials, setPendingCredentials] = useState<{
    apiKey: string
  } | null>(null)

  const processCredentials = useCallback(
    (credentials: { apiKey: string }) => {
      const newConfiguration: SphereIntegrationConfig = {
        apiKey: credentials.apiKey,
        obfuscatedApiKey: sphereConfiguration.obfuscatedApiKey || ''
      }
      updateConfiguration(newConfiguration)
      updateValidationState(true)
    },
    [
      updateConfiguration,
      updateValidationState,
      sphereConfiguration.obfuscatedApiKey
    ]
  )

  useEffect(() => {
    if (pendingCredentials) {
      processCredentials(pendingCredentials)
      setPendingCredentials(null)
    }
  }, [pendingCredentials, processCredentials])

  const updateCredentials = useCallback((credentials: { apiKey: string }) => {
    setWasConnectionSuccessful(true)
    setPendingCredentials(credentials)
  }, [])

  const resetValidationState = useCallback(() => {
    if (wasConnectionSuccessful) {
      updateValidationState(false)
      setWasConnectionSuccessful(false)
    }
  }, [wasConnectionSuccessful, updateValidationState])

  const existingCredentials = useMemo(
    () => ({
      apiKey: sphereConfiguration.obfuscatedApiKey
    }),
    [sphereConfiguration.obfuscatedApiKey]
  )

  return (
    <Flex grow={1} width="100%">
      <Flex flexDirection="column" gap="20px" grow={1}>
        <SphereAuthenticationCard
          onConnectionSuccess={updateCredentials}
          onCredentialsChange={resetValidationState}
          existingCredentials={existingCredentials}
        />
      </Flex>
    </Flex>
  )
}
